<template>
  <base-layout>
    <template v-slot:header>
      Services
    </template>
    <template v-slot:main>
      <div>
        <div class="flex flex-col md:flex-row mt-4 md:-mt-4 gap-2">
          <div v-for="(column, colIndex) in categoryColumns" :key="colIndex" class="flex-1 flex flex-col gap-2">
            <!-- Real href for crawlers; click opens modal for in-app users -->
            <a
              v-for="category in column"
              :key="category.categorySlug"
              :href="category.href"
              :id="category.catName"
              class="cursor-pointer block"
              @click.prevent="openCategoryModal(category)"
            >
              <div class="flex overflow-hidden w-full rounded-lg shadow-lg bg-white hover:bg-gray-50">
                <div class="flex items-center p-4 flex-1">
                  <img
                    class="w-12 object-fill mr-4"
                    :src="category.imgName"
                    :title="category.catName"
                    :alt="category.catName"
                  >
                  <h2 class="font-bold text-gray-900">{{ category.catName }}</h2>
                </div>
                <div class="flex items-center justify-center bg-red-600 text-white w-12">
                  <span class="text-sm font-semibold">{{ category.companies.length }}</span>
                </div>
              </div>
            </a>
          </div>
        </div>

        <div class="clear-both"></div>

        <modal
          v-show="showModal"
          @submit="showModal = false"
          @cancel="showModal = false"
          :doSkipHeader="true"
          :doSkipCancel="true"
          customCssClasses="sm:max-w-3xl  z-40"
          affirmativeButtonLabel="Close"
        >
          <template v-slot:body>
            <div class="flex items-center mb-6 w-full sticky top-0 bg-white z-50 p-4">
              <img
                v-if="showModal && selectedCategory"
                class="w-12 h-12 object-contain mr-2"
                :src="selectedCategory.imgName"
                :title="selectedCategory.catName"
              >
              <h2 class="font-bold text-lg text-red-700">
                {{ selectedCategory ? selectedCategory.catName : '' }}
              </h2>
            </div>

            <div class="grid grid-cols-1 gap-4">
              <div
                v-for="serviceCompany in (selectedCategory ? selectedCategory.companies : [])"
                :key="serviceCompany.id"
                class="flex flex-col md:flex-row md:justify-between md:items-center p-2 border-b border-gray-200"
              >
                <!-- Real href for crawlers; click opens company modal for in-app users -->
                <a
                  :href="serviceCompany.companyHref"
                  class="cursor-pointer text-base md:text-sm truncate font-bold text-gray-800 sm:text-gray-500"
                  @click.prevent="fetchCompanyAndDisplayModal(serviceCompany.id)"
                >
                  {{ serviceCompany.name }}
                </a>
                <div class="flex items-start mt-1 md:mt-0 md:w-32 text-sm text-gray-600">
                  <svg class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="leading-tight">
                    {{ processCountryName(serviceCompany.compCountry) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="mt-6 text-center text-sm cursor-pointer">
              To be listed in this category, please contact
              <a
                :href="`mailto:rhody@thebrokersite.com?Subject=TBS Service listing in category ${selectedCategory ? selectedCategory.catName : ''}`"
                class="text-red-600"
              >
                rhody@thebrokersite.com
              </a>
            </div>
          </template>
        </modal>

        <modal
          v-show="showCompanyModal"
          @submit="closeCompanyModal"
          @cancel="closeCompanyModal"
          :doSkipHeader="true"
          :doSkipCancel="true"
          customCssClasses="sm:max-w-6xl  z-50"
          affirmativeButtonLabel="Close"
        >
          <template v-slot:body>
            <CompanyBody
              ref="companyBody"
              :isInModal="true"
              :selectedCompanyEnriched="selectedCompany"
              :contactsEnriched="selectedCompanyContacts"
              @openModal="openContactModal"
            />
          </template>
        </modal>

        <modal-contact ref="contactModal" action="sendMailToContact"></modal-contact>
      </div>
    </template>
  </base-layout>
</template>

<script>
import axios from 'axios'
import { mapActions, mapState, mapMutations } from 'vuex'
import BaseLayout from '@/components/BaseLayout.vue'
import Modal from '@/components/modal.vue'
import CompanyBody from '@/components/CompanyBody.vue'
import ModalContact from '@/components/ModalContact.vue'
import { useSchemaOrg } from '../composables/useSchema.org.js'
import { groupServicesByCategory } from '@/utils/serviceDirectory'

export default {
  name: 'Services',
  components: {
    ModalContact,
    BaseLayout,
    Modal,
    CompanyBody,
  },
  data: () => ({
    serviceCompanies: null,
    selectedCompanyContacts: [],
    selectedCompany: {},
    showCompanyModal: false,
    showModal: false,
    selectedCategory: null,
  }),
  computed: {
    ...mapState(['route', 'user', 'expandedCatId', 'categories']),
    serviceCategories() {
      return groupServicesByCategory(this.serviceCompanies || [])
    },
    categoryColumns() {
      const cats = this.serviceCategories
      const columnSize = Math.ceil(cats.length / 3)

      return [
        cats.slice(0, columnSize),
        cats.slice(columnSize, columnSize * 2),
        cats.slice(columnSize * 2)
      ]
    },
  },
  watch: {
    '$route.hash': 'onRouteHashChange',
  },
  mounted() {
    this.onRouteHashChange()
  },
  async created() {
    this.serviceCompanies = (await axios.get(`/api/services`)).data.payload
    this.addServicePageSchema()
  },
  methods: {
    ...mapActions(['getCompany']),
    ...mapMutations(['setExpandedCategory']),
    openCategoryModal(category) {
      this.selectedCategory = category
      this.showModal = true
    },
    openContactModal(contact) {
      this.$refs.contactModal.openSendMessageModal(contact)
    },
    onRouteHashChange() {
      const categoryName = decodeURIComponent(this.$route.hash.replace('#', '')).replace(/_/g, ' ')
      const category = this.categories.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase())
      if (category) {
        this.setExpandedCategory(category.id)
        this.$nextTick(() => {
          const element = document.getElementById(categoryName)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        })
      }
    },
    fetchCompanyAndDisplayModal(id) {
      this.getCompany({ id, fromServices: true }).then(resp => {
        if (resp.status !== 200) throw new Error('TODO: not found. show error page')
        this.selectedCompany = resp.company
        this.selectedCompanyContacts = resp.contacts
        this.showCompanyModal = true
      })
    },
    closeCompanyModal() {
      this.showCompanyModal = false
    },
    processCountryName(strCountry) {
      switch (strCountry) {
        case 'United Arab Emirates':
          return 'UAE'
        case 'United States':
          return 'USA'
        case 'United Kingdom':
          return 'UK'
        default:
          return strCountry
      }
    },
    addServicePageSchema() {
      const categories = this.serviceCategories
      const serviceSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'IT Services Directory - The Broker Site',
        description: 'Find trusted IT service providers worldwide',
        url: 'https://www.thebrokersite.com/services',
        itemListElement: categories.map((category, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Service',
            name: category.catName,
            url: `https://www.thebrokersite.com${category.href}`,
            provider: category.companies.map(company => ({
              '@type': 'Organization',
              name: company.name,
              url: `https://www.thebrokersite.com${company.companyHref}`,
            })),
          },
        })),
      }
      useSchemaOrg(serviceSchema)
    },
  },
}
</script>
<style scoped>
.fade-enter-active {
  transition: opacity .5s;
}

.fade-leave-active {
  transition: opacity .0s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0;
}
</style>
